<script setup>
import useNodeProps from "@/Composables/useNodeProps"
import { FormBuilder, useSchema } from '@codinglabsau/inertia-form-builder'
import Carousel from "@/Components/Inputs/BlockEditor/Extension/Components/Sales/Carousel.vue"
import EditableNodeWrapper from "@/Components/Inputs/BlockEditor/Extension/Components/EditableNodeWrapper.vue"
import { onMounted, ref, watch } from "vue"
import { PrimaryButton, Modal, Container, NumberRange, Error } from "@codinglabsau/ui"
import { RectangleStackIcon, PlusIcon } from "@heroicons/vue/24/outline"
import { NodeViewContent, nodeViewProps, NodeViewWrapper } from "@tiptap/vue-3"

const props = defineProps({
  ...nodeViewProps,
})

let {
  cols,
  spacing,
} = useNodeProps(props)

const validationError = ref('')

// search sales
const selectedSales = ref([])

const defaultPrices = Array.isArray(props.node.attrs.prices) ? props.node.attrs.prices : (typeof props.node.attrs.prices === 'string' ? props.node.attrs.prices.split(',') : [0, 0])

const priceRangeRef = ref([])

const showSelectSalesModal = ref(false)

const schema = useSchema({
  price: {
    component: NumberRange,
    value: [defaultPrices[0], defaultPrices[1]],
    props: {
      description:'Type your minimum and maximum price or leave them empty',
      from: 0,
      to: 0,
      min: 0,
      max: 10000000,
      step: '1'
    }
  }
})

onMounted(() => {
  if (props.extension.options.data.hasOwnProperty(props.node.attrs.id)) {
    selectedSales.value = [...props.extension.options.defaultSales]
  }
})

const openSelectSalesModal = () => {
  showSelectSalesModal.value = ! showSelectSalesModal.value
}

const closeSelectSalesModal = () => {
  showSelectSalesModal.value = false
  schema.form.reset()
  schema.form.price = [defaultPrices[0], defaultPrices[1]]
}

const addSales = (sales) => {
  selectedSales.value = []
  selectedSales.value = sales
}

const submit = () => {
  if (schema.form?.price && schema.form?.price[0] || schema.form?.price && schema.form?.price[1]) {
    if (schema.form?.price[0] && schema.form?.price && schema.form?.price[1] && schema.form?.price[0] >= schema.form?.price[1]) {
      validationError.value = __('The minimum price should less than the maximum price.')
    } else {
      priceRangeRef.value = [
        schema.form?.price && schema.form?.price[0] ? schema.form?.price[0] : 0,
        schema.form?.price && schema.form?.price[1] ? schema.form?.price[1] : null
      ]
      schema.form.reset()
      showSelectSalesModal.value = false
      selectedSales.value = props.extension.options.defaultSales
    }
  } else {
    priceRangeRef.value = [0, null]
    schema.form.reset()
    showSelectSalesModal.value = false
    selectedSales.value = props.extension.options.defaultSales
  }
}

watch(() => selectedSales.value, () => {
  props.updateAttributes({
    sales: selectedSales.value.map(sale => sale.id),
  })
}, {deep: true})

watch(() => priceRangeRef.value, () => {
  props.updateAttributes({
    prices: priceRangeRef.value
  })
}, {deep: true})

</script>

<template>
  <node-view-wrapper>
    <EditableNodeWrapper v-bind="props">
      <template #tools>
        <button
          @click.prevent="openSelectSalesModal"
          type="button"
          class="p-1"
          v-tooltip="__('Select Sales')"
        >
          <PlusIcon class="-ml-0.5 mr-1.5 h-5 w-5" aria-hidden="true"/>
        </button>
      </template>
      <template #content>
        <div class="w-full">
          <div class="flex w-full">
            <node-view-content class="first:prose-headings:mt-2"/>
          </div>
          <div v-if="selectedSales.length > 0">
            <Carousel :sales="selectedSales"/>
          </div>

          <div v-else class="my-4 text-center space-y-1">
            <RectangleStackIcon class="mx-auto w-12 h-12 text-gray-300"/>
            <h3 class="text-sm font-semibold text-gray-600">
              {{ __('No Sales selected') }}
            </h3>
            <p class="text-gray-400 text-sm">
              {{ __('Select or search a Sale') }}
            </p>
            <PrimaryButton
              @click.prevent="openSelectSalesModal"
              type="button"
              as="button"
            >
              <PlusIcon class="-ml-0.5 mr-1.5 h-5 w-5" aria-hidden="true"/>
              {{ __('Select Sales') }}
            </PrimaryButton>
          </div>
        </div>
      </template>
    </EditableNodeWrapper>
  </node-view-wrapper>
  <Modal :open="showSelectSalesModal" @close="closeSelectSalesModal">
      <Container>
        <Error v-if="validationError" :error="validationError" v-html="validationError"
               class="!text-red-800 pb-2" />
        <h3 class="text-md leading-6 font-medium text-gray-900">
          {{ __('Search sales') }}
        </h3>
        <form @submit.prevent="submit">
          <FormBuilder :schema="schema"/>
        </form>
      </Container>
  </Modal>
</template>
